import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FEATURE_KEYS, FEATURE_LABELS } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Shield, LogOut, Building2, CheckCircle2, Ban, CalendarPlus, Pencil, Bell, Eye,
  TrendingUp, DollarSign, Users, Percent, Megaphone, Trash2, ScrollText,
} from "lucide-react";
import { toast } from "sonner";

const StatBox = ({ label, value, icon: Icon }) => (
  <div className="border border-border bg-card p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</div>
      {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
    </div>
    <div className="font-mono text-2xl font-bold mt-2">{value}</div>
  </div>
);

function statusBadge(o) {
  if (o.read_only && o.plan === "demo") return <span className="text-xs font-mono uppercase bg-secondary px-2 py-0.5">DEMO</span>;
  if (o.status === "suspended") return <span className="text-xs font-mono uppercase bg-destructive text-white px-2 py-0.5">SUSPENDED</span>;
  if (o.is_expired) return <span className="text-xs font-mono uppercase bg-amber-400 text-black px-2 py-0.5">EXPIRED</span>;
  return <span className="text-xs font-mono uppercase bg-success text-white px-2 py-0.5">ACTIVE</span>;
}

function Overview({ analytics }) {
  if (!analytics) return <div className="font-mono text-sm text-muted-foreground py-8">Loading analytics…</div>;
  const maxSignup = Math.max(1, ...analytics.signups.map((s) => s.count));
  const plans = Object.entries(analytics.plan_distribution || {});
  return (
    <div className="space-y-8" data-testid="sa-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatBox label="MRR" value={`$${analytics.mrr.toLocaleString()}`} icon={DollarSign} />
        <StatBox label="ARR" value={`$${analytics.arr.toLocaleString()}`} icon={TrendingUp} />
        <StatBox label="Paying Orgs" value={analytics.paying_orgs} icon={Users} />
        <StatBox label="Conversion" value={`${analytics.conversion_rate}%`} icon={Percent} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-4">Signups · last 6 months</div>
          <div className="flex items-end gap-3 h-40">
            {analytics.signups.map((s) => (
              <div key={s.key} className="flex-1 flex flex-col items-center gap-2" data-testid={`signup-bar-${s.key}`}>
                <div className="text-xs font-mono font-bold">{s.count}</div>
                <div className="w-full bg-primary transition-all" style={{ height: `${(s.count / maxSignup) * 100}%`, minHeight: s.count ? "4px" : "0" }} />
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-4">Plan distribution</div>
          <div className="space-y-3">
            {plans.length === 0 && <div className="text-sm text-muted-foreground">No paying plans yet.</div>}
            {plans.map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between text-sm">
                <span className="capitalize">{plan.replace("_", " ")}</span>
                <span className="font-mono font-bold">{count}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-border grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-muted-foreground text-xs uppercase tracking-wider">Revenue collected</div><div className="font-mono text-lg font-bold">${analytics.total_revenue.toLocaleString()}</div></div>
            <div><div className="text-muted-foreground text-xs uppercase tracking-wider">Paid txns</div><div className="font-mono text-lg font-bold">{analytics.paid_transactions}</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/super-admin/audit-logs?limit=200").then(({ data }) => setLogs(data)).catch(() => {}); }, []);
  return (
    <div className="border border-border bg-card overflow-x-auto" data-testid="sa-audit-logs">
      <Table>
        <TableHeader><TableRow className="bg-secondary">
          <TableHead>When</TableHead><TableHead>Actor</TableHead><TableHead>Role</TableHead>
          <TableHead>Action</TableHead><TableHead>Detail</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No activity recorded yet.</TableCell></TableRow>}
          {logs.map((l) => (
            <TableRow key={l.id} data-testid="audit-row">
              <TableCell className="font-mono text-xs whitespace-nowrap">{(l.created_at || "").slice(0, 19).replace("T", " ")}</TableCell>
              <TableCell className="text-sm">{l.actor_email}</TableCell>
              <TableCell className="text-xs capitalize text-muted-foreground">{(l.actor_role || "").replace("_", " ")}</TableCell>
              <TableCell className="font-mono text-xs">{l.action}</TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground truncate max-w-[240px]">{l.detail}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function Announcements() {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");
  const [tone, setTone] = useState("info");
  const load = useCallback(() => { api.get("/super-admin/announcements").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!msg.trim()) return;
    try { await api.post("/super-admin/announcements", { message: msg, tone, active: true }); toast.success("Announcement published"); setMsg(""); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggle = async (a) => {
    try { await api.put(`/super-admin/announcements/${a.id}`, { message: a.message, tone: a.tone, active: !a.active }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (a) => {
    try { await api.delete(`/super-admin/announcements/${a.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toneClass = { info: "bg-secondary", warn: "bg-amber-400 text-black", error: "bg-destructive text-white" };
  return (
    <div className="space-y-6" data-testid="sa-announcements">
      <div className="border border-border bg-card p-5">
        <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-3">Broadcast a message to all organizations</div>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="e.g. Scheduled maintenance this Sunday 2–3 AM WAT." className="rounded-none" data-testid="announcement-message-input" />
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <Select value={tone} onValueChange={setTone}>
            <SelectTrigger className="rounded-none w-40" data-testid="announcement-tone-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warn">Warning</SelectItem>
              <SelectItem value="error">Critical</SelectItem>
            </SelectContent>
          </Select>
          <Button className="rounded-none gap-2" onClick={create} data-testid="announcement-publish-btn"><Megaphone className="w-4 h-4" /> Publish</Button>
        </div>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-sm text-muted-foreground">No announcements yet.</div>}
        {items.map((a) => (
          <div key={a.id} className="border border-border bg-card p-3 flex items-center gap-3" data-testid="announcement-item">
            <span className={`text-xs font-mono uppercase px-2 py-0.5 ${toneClass[a.tone]}`}>{a.tone}</span>
            <span className="flex-1 text-sm">{a.message}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{a.active ? "Live" : "Hidden"}</span>
              <Switch checked={a.active} onCheckedChange={() => toggle(a)} data-testid="announcement-toggle" />
              <Button size="sm" variant="ghost" className="rounded-none text-destructive h-8" onClick={() => remove(a)} data-testid="announcement-delete"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SuperAdmin() {
  const { user, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const [s, a, o] = await Promise.all([
      api.get("/super-admin/stats"),
      api.get("/super-admin/analytics"),
      api.get("/super-admin/organizations"),
    ]);
    setStats(s.data);
    setAnalytics(a.data);
    setOrgs(o.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const quick = async (org, body) => {
    try { await api.put(`/super-admin/organizations/${org.id}`, body); toast.success("Updated"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const extend = async (org, days) => {
    try { await api.post(`/super-admin/organizations/${org.id}/extend?days=${days}`); toast.success(`Extended ${days} days & activated`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const impersonate = async (org) => {
    try {
      await api.post(`/super-admin/organizations/${org.id}/impersonate`);
      await refreshMe();
      navigate("/");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openEdit = (o) => {
    setEditing({ ...o, expires_date: (o.expires_at || "").slice(0, 10), features: { ...o.features } });
    setOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/super-admin/organizations/${editing.id}`, {
        status: editing.status,
        plan: editing.plan,
        payment_mode: editing.payment_mode,
        expires_at: editing.expires_date ? new Date(editing.expires_date + "T23:59:59Z").toISOString() : undefined,
        features: editing.features,
        read_only: editing.read_only,
        billing_amount: editing.billing_amount != null && editing.billing_amount !== "" ? Number(editing.billing_amount) : undefined,
      });
      toast.success("Organization updated");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="super-admin-page">
      <header className="h-16 border-b border-border bg-card flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary flex items-center justify-center"><Shield className="w-5 h-5 text-primary-foreground" /></div>
          <span className="font-display font-black text-lg tracking-tighter">StockLedger · Platform Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground hidden sm:inline">{user.name}</span>
          <Button variant="outline" className="rounded-none gap-2" onClick={doLogout} data-testid="sa-logout-btn"><LogOut className="w-4 h-4" /> Sign out</Button>
        </div>
      </header>

      <main className="p-4 lg:p-8 relative z-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Platform</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Command Center</h1>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
            <StatBox label="Total Orgs" value={stats.total_orgs} />
            <StatBox label="Active" value={stats.active} />
            <StatBox label="Trial" value={stats.trial} />
            <StatBox label="Expired" value={stats.expired} />
            <StatBox label="Suspended" value={stats.suspended} />
            <StatBox label="Users" value={stats.total_users} />
          </div>
        )}

        <Tabs defaultValue="orgs">
          <TabsList className="rounded-none">
            <TabsTrigger value="overview" className="rounded-none gap-2" data-testid="tab-overview"><TrendingUp className="w-4 h-4" /> Analytics</TabsTrigger>
            <TabsTrigger value="orgs" className="rounded-none gap-2" data-testid="tab-orgs"><Building2 className="w-4 h-4" /> Organizations</TabsTrigger>
            <TabsTrigger value="audit" className="rounded-none gap-2" data-testid="tab-audit"><ScrollText className="w-4 h-4" /> Audit Logs</TabsTrigger>
            <TabsTrigger value="announcements" className="rounded-none gap-2" data-testid="tab-announcements"><Megaphone className="w-4 h-4" /> Announcements</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6"><Overview analytics={analytics} /></TabsContent>

          <TabsContent value="orgs" className="mt-6">
            <div className="border border-border bg-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-secondary">
                  <TableHead>Organization</TableHead><TableHead>Owner</TableHead><TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead><TableHead>Payment</TableHead><TableHead className="text-right">Days left</TableHead>
                  <TableHead className="text-right">Users</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {orgs.map((o) => (
                    <TableRow key={o.id} className="hover:bg-secondary/60" data-testid="sa-org-row">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />{o.name}
                          {o.renewal_requested && <Bell className="w-3.5 h-3.5 text-amber-500" title="Renewal requested" />}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{o.owner_email}</TableCell>
                      <TableCell className="text-sm capitalize">{o.plan}</TableCell>
                      <TableCell>{statusBadge(o)}</TableCell>
                      <TableCell className="text-sm capitalize">{o.payment_mode}</TableCell>
                      <TableCell className="text-right font-mono">{o.days_left ?? "—"}</TableCell>
                      <TableCell className="text-right font-mono">{o.user_count}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          {!(o.plan === "demo") && (o.status === "suspended"
                            ? <Button size="sm" variant="outline" className="rounded-none gap-1 h-8" onClick={() => quick(o, { status: "active" })} data-testid="sa-activate-btn"><CheckCircle2 className="w-3.5 h-3.5" />Activate</Button>
                            : <Button size="sm" variant="ghost" className="rounded-none gap-1 h-8 text-destructive" onClick={() => quick(o, { status: "suspended" })} data-testid="sa-suspend-btn"><Ban className="w-3.5 h-3.5" />Suspend</Button>)}
                          {!(o.plan === "demo") && <Button size="sm" variant="outline" className="rounded-none gap-1 h-8" onClick={() => extend(o, 30)} data-testid="sa-extend-btn"><CalendarPlus className="w-3.5 h-3.5" />+30d</Button>}
                          <Button size="sm" variant="ghost" className="rounded-none gap-1 h-8" onClick={() => impersonate(o)} data-testid="sa-impersonate-btn"><Eye className="w-3.5 h-3.5" />View as</Button>
                          <Button size="sm" variant="ghost" className="rounded-none gap-1 h-8" onClick={() => openEdit(o)} data-testid="sa-edit-btn"><Pencil className="w-3.5 h-3.5" />Manage</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-6"><AuditLogs /></TabsContent>
          <TabsContent value="announcements" className="mt-6"><Announcements /></TabsContent>
        </Tabs>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Manage {editing?.name}</DialogTitle>
            <DialogDescription>Set plan, expiry, payment mode and enabled features for this organization.</DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing((s) => ({ ...s, status: v }))}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="sa-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="suspended">Suspended</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Plan</Label>
                  <Select value={editing.plan} onValueChange={(v) => setEditing((s) => ({ ...s, plan: v }))}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="sa-plan-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="one_off">One-off</SelectItem>
                      <SelectItem value="monthly">Monthly retainer</SelectItem>
                      <SelectItem value="yearly">Yearly retainer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Payment mode</Label>
                  <Select value={editing.payment_mode} onValueChange={(v) => setEditing((s) => ({ ...s, payment_mode: v }))}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="sa-payment-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="manual">Manual</SelectItem><SelectItem value="online">Online</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Expires on</Label>
                  <Input type="date" className="rounded-none mt-1" value={editing.expires_date} onChange={(e) => setEditing((s) => ({ ...s, expires_date: e.target.value }))} data-testid="sa-expires-input" />
                </div>
                <div className="sm:col-span-2"><Label>Monthly revenue (USD, for MRR)</Label>
                  <Input type="number" step="0.01" className="rounded-none mt-1" value={editing.billing_amount ?? ""} placeholder="Auto from plan if empty" onChange={(e) => setEditing((s) => ({ ...s, billing_amount: e.target.value }))} data-testid="sa-billing-amount-input" />
                </div>
              </div>

              <div className="flex items-center justify-between border border-border p-3">
                <div>
                  <Label>Read-only</Label>
                  <p className="text-xs text-muted-foreground">Lets the org view but not modify data.</p>
                </div>
                <Switch checked={!!editing.read_only} onCheckedChange={(v) => setEditing((s) => ({ ...s, read_only: v }))} data-testid="sa-readonly-switch" />
              </div>

              <div className="border border-border p-3">
                <Label className="text-xs uppercase tracking-wider">Enabled features</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                  {FEATURE_KEYS.map((k) => (
                    <label key={k} className="flex items-center justify-between gap-2 text-sm">
                      <span>{FEATURE_LABELS[k]}</span>
                      <Switch checked={editing.features?.[k] !== false}
                        onCheckedChange={(v) => setEditing((s) => ({ ...s, features: { ...s.features, [k]: v } }))}
                        data-testid={`sa-feature-${k}`} />
                    </label>
                  ))}
                </div>
              </div>

              <DialogFooter><Button type="submit" className="rounded-none" data-testid="sa-save-btn">Save Changes</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
